import { useRef, useEffect, useCallback } from "react";
import { uiFactory } from "@/ui";
import Close from "assets/icons/close_large.svg?react";
import { useTransparentize } from "@/theme/utils/transparentize";
import { dataSyncEmitter } from "@/modules/events/emitter";
import { t } from "@/utils";
import IconButton from "@/components/IconButton";
import { useCamera } from "camera/store/camera";
import { useCameraMethods } from "..";

const EnhancedVideo = uiFactory("video");

export default function RemoteStreamPlayer() {
  const videoStream = useRef(new MediaStream()).current;
  const audioStream = useRef(new MediaStream()).current;
  const tracks = useRef<{ [id: string]: boolean }>({});
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const activeFaceJid = useCamera((state) => state.activeFaceJid);
  const stopFace = useCameraMethods((state) => state.stopFace);

  const bg = useTransparentize("neutral-variant.30", 0.7);

  const addRemoteTrack = useCallback(
    (track: MediaStreamTrack) => {
      if (tracks.current[track.id]) return;
      log.monitoring("Adding remote track", track.id, track.kind, track.readyState);

      tracks.current[track.id] = true;
      if (track.kind === "audio") audioStream.addTrack(track);
      else videoStream.addTrack(track);
    },
    [videoStream, audioStream]
  );

  const removeRemoteTrack = useCallback(
    (track: MediaStreamTrack) => {
      log.monitoring("Deleteing remote track", track.id);
      delete tracks.current[track.id];
      if (track.kind === "audio") audioStream.removeTrack(track);
      else videoStream.removeTrack(track);
    },
    [videoStream, audioStream]
  );

  useEffect(() => {
    const unsub = [
      dataSyncEmitter.on("add-remote-track", addRemoteTrack),
      dataSyncEmitter.on("remove-remote-track", removeRemoteTrack)
    ];

    return () => unsub.forEach((cb) => cb());
  }, [addRemoteTrack, removeRemoteTrack, videoStream, audioStream]);

  useEffect(() => {
    if (remoteVideoRef.current) {
      log.info("Preparing for remote video stream");
      remoteVideoRef.current.srcObject = videoStream;
    }
    if (remoteAudioRef.current) {
      log.info("Preparing for remote audio stream");
      remoteAudioRef.current.srcObject = audioStream;
    }
  }, [videoStream, audioStream]);

  const hasFaceActive = Boolean(activeFaceJid);
  return (
    <>
      {hasFaceActive && (
        <IconButton
          icon={Close}
          tooltip={{ label: t("buttons.close") }}
          fill="neutral.99"
          bg={bg}
          onClick={stopFace}
          props={{
            pos: "absolute",
            zIndex: 3,
            top: "2rem",
            left: "2rem"
          }}
        />
      )}
      <EnhancedVideo
        ref={remoteVideoRef}
        autoPlay
        muted
        pos="absolute"
        bottom="0"
        right="0"
        w={hasFaceActive ? "100%" : "0px"}
        h={hasFaceActive ? "100%" : "0px"}
        zIndex={hasFaceActive ? 2 : -1}
        objectFit="cover"
        borderRadius="16px"
      />
      <audio ref={remoteAudioRef} autoPlay />
    </>
  );
}
